import { ParsedCSVData, ValidationError } from '@/types/upload';
import {
  SeriesNameSchema,
  SpecialBlockTypeSchema,
  SpecialItemTypeSchema,
  SpecialTypeSchema,
} from '@/types/api.zod';
import { type CsvRow, toCsvRow } from '@/utils/csvRow';

/**
 * 特集ページ（special / campaign）CSV のクライアント側バリデーション。
 *
 * S3 へ presigned URL でアップロードする前に、Book / Product / Menu と同様に全ルールを
 * クライアントで検証してプレビュー可否を判定する。サーバー（ProcessUpload Lambda /
 * SpecialUploadService）でも同一ルールを再検証する（多層防御）。
 *
 * 仕様の正本: docs/features/special.md「CSV フォーマット」「CSV バリデーションルール」。
 * special_type / block_type / item_type の許容値は codegen の Zod enum（単一の出所）から導出する。
 *
 * 権威はサーバー（special_service.py）側にあり、ここはアップロード前の UX 向け事前検証。
 * slug / platform / 色 / 日時 / 組合せ等のルールはバックエンドと手動ミラーのため、
 * 仕様変更時は docs/features/special.md を基点に special_service.py と本ファイルの両方を更新すること。
 */

const COLUMNS = [
  'data_type',
  'special_type',
  'special_name',
  'start_at',
  'stop_at',
  'special_banner',
  'block_id',
  'title',
  'block_type',
  'item_type',
  'item',
  'item_link_to',
  'item_link_target',
  'platform',
  'exclude_on_list',
  'series_name',
  'product_group',
  'background_color',
  'text_color',
  'block_header_background',
  'block_header_txt_color',
  'btn_txt_color',
  'tbtn_background',
  'rbtn_background',
  'fbtn_background',
  'cbtn_background',
  'note',
] as const;

// 列名をキーに持つ 1 行分の型（値は常に string）。詳細は utils/csvRow.ts。
type SpecialColumn = (typeof COLUMNS)[number];
type SpecialRow = CsvRow<typeof COLUMNS>;

// 色スコープ: background_color（背景）は PAGE のみ。それ以外の全色（text / 見出し背景・文字色 /
// ボタン文字色・各ボタン背景）は PAGE / BLOCK / ITEM の 3 レベルすべてで指定可（各 optional。
// 優先順位 ITEM > BLOCK > PAGE の上書き適用は配信 / 事前生成時の責務）。
const NON_BACKGROUND_COLOR_COLUMNS = [
  'text_color',
  'block_header_background',
  'block_header_txt_color',
  'btn_txt_color',
  'tbtn_background',
  'rbtn_background',
  'fbtn_background',
  'cbtn_background',
] as const;
const PAGE_COLOR_COLUMNS = ['background_color', ...NON_BACKGROUND_COLOR_COLUMNS] as const;
const BLOCK_COLOR_COLUMNS = NON_BACKGROUND_COLOR_COLUMNS;
const ITEM_COLOR_COLUMNS = NON_BACKGROUND_COLOR_COLUMNS;

// data_type 別の required / not_include（docs/features/special.md）。
// PAGE の series_name は special_type=product のときのみ必須（validatePageRow で判定）。
// 列名を SpecialColumn に固定しているため、COLUMNS に無い列名を書くとコンパイルエラーになる。
type RowRule = { required: SpecialColumn[]; notInclude: SpecialColumn[] };
const ROW_RULES: Record<string, RowRule> = {
  PAGE: {
    required: ['special_type', 'special_name', 'start_at', 'platform', 'title'],
    // 色は background_color を含め全色 PAGE で指定可。not_include は BLOCK / ITEM 専用列のみ。
    notInclude: ['block_id', 'block_type', 'item_type', 'item', 'item_link_to', 'item_link_target'],
  },
  BLOCK: {
    required: ['block_id', 'block_type'],
    // background_color は PAGE 専用のため BLOCK 不可。その他の色は指定可。
    notInclude: [
      'special_type',
      'special_name',
      'special_banner',
      'exclude_on_list',
      'series_name',
      'product_group',
      'background_color',
      'item_type',
      'item',
      'item_link_to',
      'item_link_target',
    ],
  },
  ITEM: {
    required: ['item_type', 'item'],
    // background_color は PAGE 専用のため ITEM 不可。その他の色（text / 見出し / ボタン）は指定可。
    notInclude: [
      'special_type',
      'special_name',
      'start_at',
      'stop_at',
      'special_banner',
      'block_id',
      'block_type',
      'exclude_on_list',
      'series_name',
      'product_group',
      'background_color',
    ],
  },
};

const SLUG_RE = /^[a-z0-9_-]+$/;
const PLATFORM_RE = /^[01]{3}$/;
const COLOR_RE = /^#[0-9a-fA-F]{6}$/;
const JDCN_RE = /^[a-zA-Z0-9]{20}$/;
const YOUTUBE_RE = /^https:\/\/(www\.youtube\.com|youtu\.be)\/.+/;

const CSV_DATETIME_RE =
  /^(\d{4})[/-](\d{1,2})[/-](\d{1,2})(?:[T ](\d{1,2}):(\d{1,2})(?::(\d{1,2}))?)?$/;
// フォールバックはタイムゾーン付き ISO8601 のみ許容（bare "2026" 等は弾く。
// バックエンド datetime.fromisoformat + astimezone 相当）。日付部の存在チェック用に年月日をキャプチャする。
const ISO8601_TZ_RE =
  /^(\d{4})-(\d{2})-(\d{2})[T ]\d{2}:\d{2}(?::\d{2})?(?:\.\d+)?(?:Z|[+-]\d{2}:?\d{2})$/;
const DATETIME_FORMAT_HINT = '（例: 2026-06-01 / 2026/06/01 00:00 / 2026-06-01T00:00:00+09:00）';

const SPECIAL_TYPES: readonly string[] = SpecialTypeSchema.options;
const BLOCK_TYPES: readonly string[] = SpecialBlockTypeSchema.options;
const ITEM_TYPES: readonly string[] = SpecialItemTypeSchema.options;

// series_name の許可リスト（SBI #506）は schema（codegen）の SeriesName enum が単一の出所。
// バックエンド（special_service.py）も同じ SeriesName を参照する。値は完全一致（大文字小文字区別）。
const ALLOWED_SERIES_NAMES: ReadonlySet<string> = new Set<string>(SeriesNameSchema.options);

// block_type × item_type の組合せ規則（docs/features/special.md「行間ルール」）。
// 件数上限は #528 で全廃（旧 jdcn=ちょうど1件 / 旧 btn=1〜2件。現仕様は全ブロック「1 件以上」のみ）。
// - product_detail（旧 jdcn ブロック相当）と btn（旧 btn ブロック相当）は static で複数指定可
// - static_with_slide（旧 jdcn_with_list 相当）は product_detail のみ登録可（複数指定可。
//   続巻リストの有無はブロック側で表し、item_type は表示粒度＝単品紹介のみを表す）
const BLOCK_ITEM_RULES: Record<string, { allowed: readonly string[]; min: number }> = {
  static: {
    allowed: ['image', 'banner', 'txt', 'coupon', 'youtube', 'btn', 'product', 'product_detail'],
    min: 1,
  },
  // 画像結合表示（#510）。image / banner を 1 件以上
  static_combine: { allowed: ['image', 'banner'], min: 1 },
  slide: { allowed: ['product'], min: 1 },
  static_with_slide: { allowed: ['product_detail'], min: 1 },
};

// item に JDCN を指定する item_type（書式検証対象）
const JDCN_ITEM_TYPES: ReadonlySet<string> = new Set(['product', 'product_detail']);

// 存在しない日付（例: 2026-02-29 / 2026-06-31）を UTC ロールオーバーの有無で弾く
const isRealDate = (year: number, month: number, day: number): boolean => {
  const probe = new Date(Date.UTC(year, month - 1, day));
  return (
    probe.getUTCFullYear() === year &&
    probe.getUTCMonth() === month - 1 &&
    probe.getUTCDate() === day
  );
};

const isValidDateTime = (value: string): boolean => {
  const s = value.trim();
  const m = CSV_DATETIME_RE.exec(s);
  if (m) {
    const [, y, mo, d, h, mi, sec] = m;
    const hour = Number(h ?? 0);
    const minute = Number(mi ?? 0);
    const second = Number(sec ?? 0);
    if (hour > 23 || minute > 59 || second > 59) return false;
    return isRealDate(Number(y), Number(mo), Number(d));
  }
  const iso = ISO8601_TZ_RE.exec(s);
  if (!iso) return false;
  const [, y, mo, d] = iso;
  return isRealDate(Number(y), Number(mo), Number(d)) && !Number.isNaN(Date.parse(s));
};

// CSV のタイムゾーン無し日時は JST（UTC+9）とみなす（サーバ DateTimeUtils と一致）。
const JST_OFFSET_MS = 9 * 60 * 60 * 1000;

// 逆転（start_at > stop_at）判定用。ミリ秒 epoch へ変換（TZ なしは JST とみなす）。
// 壁時計時刻を Date.UTC で組み立てて JST オフセットを引く（menuCsvValidation と同一方式）。
// 文字列連結 + padStart を使わないため、正規表現キャプチャの型を緩める必要がない。
const toEpoch = (value: string): number => {
  const s = value.trim();
  const m = CSV_DATETIME_RE.exec(s);
  if (m) {
    const [, y, mo, d, h, mi, sec] = m;
    return (
      Date.UTC(Number(y), Number(mo) - 1, Number(d), Number(h ?? 0), Number(mi ?? 0), Number(sec ?? 0)) -
      JST_OFFSET_MS
    );
  }
  return Date.parse(s);
};

const err = (row: number, column: string, value: string, error: string): ValidationError => ({
  row,
  column,
  value,
  error,
});

const validateRequiredNotInclude = (
  row: number,
  dataType: string,
  rule: RowRule,
  d: SpecialRow,
  errors: ValidationError[]
): void => {
  rule.required.forEach((col) => {
    if (!d[col]) errors.push(err(row, col, d[col], `${dataType} には ${col} が必須です`));
  });
  rule.notInclude.forEach((col) => {
    if (d[col]) errors.push(err(row, col, d[col], `${dataType} 行に ${col} は指定できません`));
  });
};

// 必須チェックは validateRequiredNotInclude に集約。以下は形式のみ（値がある時だけ）検証。
const validateDateTime = (
  row: number,
  value: string,
  field: string,
  errors: ValidationError[]
): void => {
  if (value && !isValidDateTime(value)) {
    errors.push(err(row, field, value, `${field} の日時形式が不正です${DATETIME_FORMAT_HINT}`));
  }
};

const validatePlatform = (row: number, value: string, errors: ValidationError[]): void => {
  if (!value) return;
  if (!PLATFORM_RE.test(value)) {
    errors.push(
      err(row, 'platform', value, 'platform は 3 桁（web/ios/android, 0/1）で指定してください')
    );
  } else if (value === '000') {
    errors.push(err(row, 'platform', value, 'platform は 000 不可（最低 1 つは表示）'));
  }
};

const validateColors = (
  row: number,
  d: SpecialRow,
  columns: readonly SpecialColumn[],
  errors: ValidationError[]
): void => {
  columns.forEach((col) => {
    if (d[col] && !COLOR_RE.test(d[col])) {
      errors.push(
        err(row, col, d[col], `${col} は #RRGGBB 形式（# + 16 進 6 桁）で指定してください`)
      );
    }
  });
};

const validatePageRow = (
  row: number,
  d: SpecialRow,
  errors: ValidationError[]
): void => {
  if (d.special_type && !SPECIAL_TYPES.includes(d.special_type)) {
    errors.push(
      err(
        row,
        'special_type',
        d.special_type,
        `special_type は ${SPECIAL_TYPES.join(' / ')} のいずれかです`
      )
    );
  }
  if (d.special_name && !SLUG_RE.test(d.special_name)) {
    errors.push(
      err(row, 'special_name', d.special_name, 'special_name は a-z / 0-9 / _ / - のみ使用できます')
    );
  }
  if (d.special_type === 'product' && !d.series_name) {
    errors.push(
      err(row, 'series_name', '', 'special_type=product の PAGE には series_name が必須です')
    );
  }
  // series_name は許可リストの値のみ（SBI #506）。値がある場合のみ検証する。
  if (d.series_name && !ALLOWED_SERIES_NAMES.has(d.series_name)) {
    errors.push(
      err(
        row,
        'series_name',
        d.series_name,
        `series_name '${d.series_name}' は許可されていません（定義済みのシリーズ名のみ指定できます）`
      )
    );
  }
  validateDateTime(row, d.start_at, 'start_at', errors);
  validateDateTime(row, d.stop_at, 'stop_at', errors);
  // PAGE は start_at > stop_at（逆転）をエラーとする（BLOCK は表示対象外扱いでエラーにしない）
  if (d.start_at && d.stop_at && isValidDateTime(d.start_at) && isValidDateTime(d.stop_at)) {
    if (toEpoch(d.start_at) > toEpoch(d.stop_at)) {
      errors.push(
        err(row, 'stop_at', d.stop_at, 'start_at が stop_at より後です（公開期間が逆転しています）')
      );
    }
  }
  validatePlatform(row, d.platform, errors);
  if (d.exclude_on_list && !['true', 'false'].includes(d.exclude_on_list.toLowerCase())) {
    errors.push(
      err(
        row,
        'exclude_on_list',
        d.exclude_on_list,
        'exclude_on_list は true / false / 空欄のいずれかです'
      )
    );
  }
  if (d.product_group) {
    d.product_group.split(',').forEach((raw) => {
      const group = raw.trim();
      if (!group || !SLUG_RE.test(group)) {
        errors.push(
          err(
            row,
            'product_group',
            group,
            `product_group の要素 '${group}' は a-z / 0-9 / _ / - のみ使用できます`
          )
        );
      }
    });
  }
  validateColors(row, d, PAGE_COLOR_COLUMNS, errors);
};

const validateBlockRow = (
  row: number,
  d: SpecialRow,
  errors: ValidationError[]
): void => {
  if (d.block_id && !SLUG_RE.test(d.block_id)) {
    errors.push(err(row, 'block_id', d.block_id, 'block_id は a-z / 0-9 / _ / - のみ使用できます'));
  }
  if (d.block_type && !BLOCK_TYPES.includes(d.block_type)) {
    errors.push(
      err(
        row,
        'block_type',
        d.block_type,
        `block_type は ${BLOCK_TYPES.join(' / ')} のいずれかです`
      )
    );
  }
  validatePlatform(row, d.platform, errors);
  // BLOCK の start_at > stop_at（逆転）はエラーではなく配信時に表示対象外（docs/features/special.md）
  validateDateTime(row, d.start_at, 'start_at', errors);
  validateDateTime(row, d.stop_at, 'stop_at', errors);
  validateColors(row, d, BLOCK_COLOR_COLUMNS, errors);
};

const validateItemRow = (
  row: number,
  d: SpecialRow,
  errors: ValidationError[]
): void => {
  if (d.item_type && !ITEM_TYPES.includes(d.item_type)) {
    errors.push(
      err(row, 'item_type', d.item_type, `item_type は ${ITEM_TYPES.join(' / ')} のいずれかです`)
    );
  }
  // youtube は YouTube URL を item に指定する（item は ITEM 共通で必須。item_link_to / target は不要。#509）
  if (d.item_type === 'youtube' && d.item && !YOUTUBE_RE.test(d.item)) {
    errors.push(
      err(
        row,
        'item',
        d.item,
        'item_type=youtube の item は YouTube URL（https://www.youtube.com/... / https://youtu.be/...）で指定してください'
      )
    );
  }
  if (JDCN_ITEM_TYPES.has(d.item_type) && d.item && !JDCN_RE.test(d.item)) {
    errors.push(
      err(row, 'item', d.item, `item の JDCN 書式が不正です: '${d.item}'（英数字 20 文字）`)
    );
  }
  validatePlatform(row, d.platform, errors);
  validateColors(row, d, ITEM_COLOR_COLUMNS, errors);
};

export const validateSpecialCsv = (data: ParsedCSVData): ValidationError[] => {
  const errors: ValidationError[] = [];

  // ヘッダー（順序込み完全一致）
  const headerMatches =
    data.headers.length === COLUMNS.length && COLUMNS.every((c, i) => data.headers[i] === c);
  if (!headerMatches) {
    return [
      err(
        1,
        'ヘッダー',
        data.headers.join(','),
        `CSVヘッダーが不正です。期待: ${COLUMNS.join(',')}`
      ),
    ];
  }

  if (data.rows.length === 0) {
    return [
      err(1, 'データ', '', 'データ行がありません（1=定義名 / 2=表示名 / 3 行目以降=データ）'),
    ];
  }

  let pageCount = 0;
  const seenBlockIds = new Set<string>();
  const blocks: { row: number; blockType: string; itemTypes: string[] }[] = [];
  let currentBlock: { row: number; blockType: string; itemTypes: string[] } | null = null;

  // parseCSV が 2 行目（表示名）をスキップ済みのため data.rows[0] は実ファイル 3 行目に相当する。
  data.rows.forEach((row, idx) => {
    const rowNum = idx + 3;

    if (row.length !== COLUMNS.length) {
      errors.push(
        err(
          rowNum,
          '列数',
          row.join(','),
          `列数が不正です（${row.length} 列、期待 ${COLUMNS.length} 列）`
        )
      );
      return;
    }

    const d = toCsvRow(COLUMNS, row);

    const rule = ROW_RULES[d.data_type];
    if (!rule) {
      errors.push(
        err(rowNum, 'data_type', d.data_type, 'data_type が不正です（PAGE / BLOCK / ITEM のみ）')
      );
      return;
    }

    validateRequiredNotInclude(rowNum, d.data_type, rule, d, errors);

    switch (d.data_type) {
      case 'PAGE':
        pageCount += 1;
        if (rowNum !== 3)
          errors.push(err(rowNum, 'data_type', 'PAGE', 'PAGE 行は 3 行目のみに置けます'));
        validatePageRow(rowNum, d, errors);
        break;
      case 'BLOCK':
        if (d.block_id) {
          if (seenBlockIds.has(d.block_id)) {
            errors.push(
              err(rowNum, 'block_id', d.block_id, `block_id が重複しています: '${d.block_id}'`)
            );
          }
          seenBlockIds.add(d.block_id);
        }
        validateBlockRow(rowNum, d, errors);
        currentBlock = { row: rowNum, blockType: d.block_type, itemTypes: [] };
        blocks.push(currentBlock);
        break;
      case 'ITEM':
        validateItemRow(rowNum, d, errors);
        if (!currentBlock) {
          errors.push(
            err(rowNum, 'data_type', 'ITEM', 'ITEM 行が BLOCK より前に存在します（PAGE 直下不可）')
          );
        } else {
          currentBlock.itemTypes.push(d.item_type);
        }
        break;
    }
  });

  // 構造（行跨ぎ）
  if (pageCount === 0) {
    errors.push(err(1, 'data_type', '', 'PAGE 行がありません（CSV につき 1 行必須・3 行目）'));
  } else if (pageCount > 1) {
    errors.push(err(1, 'data_type', 'PAGE', 'PAGE 行は 1 行のみ可'));
  }
  if (blocks.length === 0) {
    errors.push(err(1, 'data_type', '', 'BLOCK 行がありません（最低 1 ブロック必須）'));
  }

  // block_type × item_type の組合せ・件数（ITEM 0 件の BLOCK もここでエラー化）
  blocks.forEach((block) => {
    const rule = BLOCK_ITEM_RULES[block.blockType];
    if (!rule) return; // block_type 不正は行バリデーション済み（組合せは判定不能のためスキップ）
    const count = block.itemTypes.length;
    if (count < rule.min) {
      errors.push(
        err(
          block.row,
          'item_type',
          '',
          `block_type=${block.blockType} の BLOCK には ITEM が ${rule.min} 件以上必要です（現在 ${count} 件）`
        )
      );
    }
    const invalid = [...new Set(block.itemTypes.filter((t) => t && !rule.allowed.includes(t)))];
    if (invalid.length > 0) {
      errors.push(
        err(
          block.row,
          'item_type',
          invalid.join(','),
          `block_type=${block.blockType} で使用できる item_type は ${rule.allowed.join(' / ')} のみです`
        )
      );
    }
  });

  return errors;
};
